'use client';

import { cn } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { usePauseOnInteraction } from '../hooks/usePauseOnInteraction';
import { useWinnersStoriesStackRotation } from '../hooks/useWinnersStoriesStackRotation';
import type { HomeWinnersStoryConfig } from '../types';
import { HomeWinnersStoryCard } from './HomeWinnersStoryCard';

type HomeWinnersStoriesAnimatedStackProps = Readonly<{
  namespace: string;
  stories: ReadonlyArray<HomeWinnersStoryConfig>;
  className?: string;
  'data-testid'?: string;
}>;

const HIDDEN_STACK_DEPTH = 3;

function HomeWinnersStoriesAnimatedStack({ namespace, stories, className, 'data-testid': dataTestId = 'home-winners-stories-stack' }: HomeWinnersStoriesAnimatedStackProps) {
  const t = useTranslations(namespace);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPaused = usePauseOnInteraction(containerRef);
  const activeIndex = useWinnersStoriesStackRotation(stories.length, isPaused);

  return (
    <div ref={containerRef} className={cn('relative isolate w-full pb-4', className)} data-testid={dataTestId} data-track-container="Winner Stories">
      <div className="grid w-full">
        {stories.map((story, storyIndex) => {
          const depth = (storyIndex - activeIndex + stories.length) % stories.length;

          return (
            <div
              key={story.id}
              className="winners-stories-stack-card col-start-1 row-start-1 justify-self-center"
              data-depth={Math.min(depth, HIDDEN_STACK_DEPTH)}
              inert={depth !== 0}
            >
              <HomeWinnersStoryCard
                href={story.href}
                avatarSrc={story.avatarSrc}
                winnerIntro={t(`stories.${story.id}.winner`)}
                prize={t(`stories.${story.id}.prize`)}
                gameLabel={t(`stories.${story.id}.game`)}
                labelColour={story.labelColour}
                data-testid={`home-winners-stories-card-${story.id}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { HomeWinnersStoriesAnimatedStack };
